import { trait } from '@traits-ts/core'
import type { Task, TaskActionButton, TaskListAction, TaskPriority, TaskType } from '../../types/task.js'

/**
 * WithUIConfiguration trait
 * Adds UI representation capabilities to tasks
 */
export const WithUIConfiguration = trait(superclass => class extends superclass {
  /**
   * Get the UI configuration for this task
   * @returns Configuration object for task UI rendering
   */
  getUIConfig() {
    return {
      icon: this.getTaskIcon(),
      color: this.getTaskColor(),
      actionButtons: this.getActionButtons(),
      listActions: this.getListActions(),
    }
  }

  /**
   * Get the icon to use for this task
   * @returns Icon class name
   */
  getTaskIcon(): string {
    const icons: Record<TaskType, string> = {
      member: 'pi pi-user',
      subkrewe: 'pi pi-users',
      admin: 'pi pi-cog',
      system: 'pi pi-server',
    }
    // `this` comes from the trait superclass, so these are untyped here.
    return icons[this.taskType as TaskType] || 'pi pi-check-circle'
  }

  /**
   * Get the color to use for this task
   * @returns CSS color value
   */
  getTaskColor(): string {
    const colors: Record<TaskPriority, string> = {
      low: 'var(--blue-500)',
      medium: 'var(--primary-color)',
      high: 'var(--orange-500)',
      critical: 'var(--red-500)',
    }
    return colors[this.priority as TaskPriority] || 'var(--primary-color)'
  }

  /**
   * Get actions for detailed task view
   * @returns Array of action buttons
   */
  getActionButtons(): TaskActionButton[] {
    const buttons: TaskActionButton[] = []

    if (this.status === 'pending') {
      buttons.push({
        label: 'Start',
        icon: 'pi pi-play',
        action: 'start',
      })
    }

    return buttons
  }

  /**
   * Get actions for task list view
   * @returns Array of list actions
   */
  getListActions(): TaskListAction[] {
    return [] // Default is no row-level actions
  }

  /**
   * Get custom component for detailed task view
   * @returns Component or null if no custom component
   */
  getTaskComponent(): unknown {
    return null // Default is no custom component
  }
})

export interface WithUIConfiguration {
  // Existing methods
  getTaskIcon(): string
  getTaskColor(): string
  getActionButtons(): TaskActionButton[]
  getListActions(): TaskListAction[]
  getDetailsComponent?: () => never

  // New method for metadata display
  getMetadataDisplayFields(): MetadataField[]
}

/**
 * Describes one metadata row in the task UI.
 *
 * The callbacks take a `Task` because that is what TaskCard passes them. They
 * used to be declared `(task: never) => boolean`, which is unusable — no value
 * is ever assignable to `never` — and the two resolver callbacks the task
 * classes actually use were missing entirely, so neither implementation could
 * annotate its return type and every `task` parameter was an implicit `any`.
 */
export interface MetadataField {
  key: string // Metadata key
  label: string // Display label
  icon?: string // Optional icon (PrimeVue icon class)
  type?: 'text' | 'date' | 'boolean' | 'status' // Data type
  /** Whether to show the field at all. Evaluated synchronously. */
  condition?: (task: Task) => boolean
  /**
   * Resolves the value to display, usually by fetching the live record rather
   * than trusting the metadata snapshot. Returns `unknown` because
   * `Task.metadata` is `Record<string, unknown>` and TaskCard renders the
   * result through template interpolation, which coerces.
   */
  displayValueResolver?: (task: Task) => unknown
  /** Async counterpart to `condition`, evaluated after the value resolves. */
  displayCondition?: (task: Task) => unknown
}
